/* =============================================================
   Pakhor — base.js
   Shared primitives: dialogs (drawer/modal), observer, fetch,
   money formatter, cart store, simple pub/sub.
   No build step. Vanilla JS, modern browsers.
   ============================================================= */

(function () {
  'use strict';

  const Pakhor = (window.Pakhor = window.Pakhor || {});
  const settings = (window.PakhorTheme = window.PakhorTheme || {});

  /* ---------- Pub/Sub ---------- */
  const channels = new Map();
  Pakhor.on = (event, fn) => {
    if (!channels.has(event)) channels.set(event, new Set());
    channels.get(event).add(fn);
    return () => channels.get(event).delete(fn);
  };
  Pakhor.emit = (event, payload) => {
    if (!channels.has(event)) return;
    channels.get(event).forEach((fn) => {
      try { fn(payload); } catch (e) { console.error('[pakhor]', event, e); }
    });
  };

  /* ---------- Money formatter ---------- */
  function formatMoneyDefault(cents) {
    const n = (cents / 100).toFixed(2);
    return `${n}`;
  }
  // Minimal Shopify money_format implementation (most common patterns).
  Pakhor.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = parseInt(cents, 10) || 0;
    const fmt = format || settings.money_format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    };

    let formatted = '';
    const placeholder = fmt.match(placeholderRegex);
    if (!placeholder) return formatWithDelimiters(value, 2);
    switch (placeholder[1]) {
      case 'amount':                  formatted = formatWithDelimiters(value, 2); break;
      case 'amount_no_decimals':      formatted = formatWithDelimiters(value, 0); break;
      case 'amount_with_comma_separator':           formatted = formatWithDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': formatted = formatWithDelimiters(value, 0, '.', ','); break;
      case 'amount_with_space_separator':           formatted = formatWithDelimiters(value, 2, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator': formatted = formatWithDelimiters(value, 0, ' ', '.'); break;
      default: formatted = formatWithDelimiters(value, 2);
    }
    return fmt.replace(placeholderRegex, formatted);
  };

  /* ---------- Fetch helpers ---------- */
  Pakhor.fetchJson = async function (url, options = {}) {
    const opts = Object.assign({ headers: {} }, options);
    opts.headers = Object.assign(
      { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      opts.headers
    );
    if (opts.body && typeof opts.body !== 'string' && !(opts.body instanceof FormData)) {
      opts.body = JSON.stringify(opts.body);
      opts.headers['Content-Type'] = 'application/json';
    }
    const res = await fetch(url, opts);
    const text = await res.text();
    let data;
    try { data = text ? JSON.parse(text) : {}; } catch (_) { data = { raw: text }; }
    if (!res.ok) {
      const err = new Error(data.message || data.description || `Request failed (${res.status})`);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  /* ---------- Cart store ---------- */
  const Cart = (Pakhor.cart = {
    state: null,
    async fetch() {
      this.state = await Pakhor.fetchJson(`${settings.routes.cart_url}.js`);
      Pakhor.emit('cart:updated', this.state);
      return this.state;
    },
    async add(items) {
      const payload = Array.isArray(items) ? { items } : { items: [items] };
      const res = await Pakhor.fetchJson(`${settings.routes.cart_add_url}.js`, {
        method: 'POST',
        body: payload
      });
      await this.fetch();
      Pakhor.emit('cart:added', res);
      return res;
    },
    async change({ id, quantity, line }) {
      const body = { quantity };
      if (line != null) body.line = line; else if (id != null) body.id = id;
      const res = await Pakhor.fetchJson(`${settings.routes.cart_change_url}.js`, {
        method: 'POST', body
      });
      this.state = res;
      Pakhor.emit('cart:updated', res);
      return res;
    },
    async update(updates) {
      const res = await Pakhor.fetchJson(`${settings.routes.cart_update_url}.js`, {
        method: 'POST',
        body: { updates }
      });
      this.state = res;
      Pakhor.emit('cart:updated', res);
      return res;
    },
    async setNote(note) {
      const res = await Pakhor.fetchJson(`${settings.routes.cart_update_url}.js`, {
        method: 'POST', body: { note }
      });
      this.state = res;
      Pakhor.emit('cart:updated', res);
      return res;
    },
    async clear() {
      const res = await Pakhor.fetchJson(`${settings.routes.cart_url}/clear.js`, { method: 'POST' });
      this.state = res;
      Pakhor.emit('cart:updated', res);
      return res;
    }
  });

  /* ---------- Dialog manager (drawer + modal) ---------- */
  const openDialogs = new Set();

  function trapFocus(el) {
    const focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    function handle(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        last.focus(); e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus(); e.preventDefault();
      }
    }
    el.addEventListener('keydown', handle);
    first.focus({ preventScroll: true });
    return () => el.removeEventListener('keydown', handle);
  }

  Pakhor.dialog = {
    open(el, { backdrop = true } = {}) {
      if (!el || el.dataset.open != null) return;
      const bd = document.querySelector('.dialog-backdrop[data-shared]') || (() => {
        const b = document.createElement('div');
        b.className = 'dialog-backdrop';
        b.dataset.shared = '';
        document.body.appendChild(b);
        b.addEventListener('click', () => Pakhor.dialog.closeTopmost());
        return b;
      })();

      el.dataset.open = '';
      if (backdrop) bd.dataset.open = '';
      document.documentElement.style.overflow = 'hidden';
      const lastFocused = document.activeElement;
      const releaseFocus = trapFocus(el);
      const onKey = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); Pakhor.dialog.close(el); }
      };
      document.addEventListener('keydown', onKey);
      openDialogs.add({ el, bd, releaseFocus, onKey, lastFocused });
      Pakhor.emit('dialog:open', { el });
    },
    close(el) {
      const entry = [...openDialogs].reverse().find((e) => e.el === el);
      if (!entry) return;
      delete entry.el.dataset.open;
      document.removeEventListener('keydown', entry.onKey);
      entry.releaseFocus();
      openDialogs.delete(entry);
      if (openDialogs.size === 0) {
        const bd = document.querySelector('.dialog-backdrop[data-shared]');
        if (bd) delete bd.dataset.open;
        document.documentElement.style.overflow = '';
      }
      if (entry.lastFocused && typeof entry.lastFocused.focus === 'function') {
        entry.lastFocused.focus({ preventScroll: true });
      }
      Pakhor.emit('dialog:close', { el });
    },
    closeTopmost() {
      const entry = [...openDialogs].pop();
      if (entry) this.close(entry.el);
    }
  };

  /* Wire any [data-dialog-open] / [data-dialog-close] declaratively.
     data-dialog-open accepts a CSS selector OR a dialog name
     matching elements with data-dialog="<name>". */
  function resolveDialog(ref) {
    if (!ref) return null;
    // Try CSS selector first
    try { const el = document.querySelector(ref); if (el) return el; } catch (_) {}
    // Fallback: match by data-dialog name (drawers/modals both use role="dialog" + data-dialog)
    const byName = document.querySelectorAll(`[data-dialog="${ref}"]`);
    for (const el of byName) { if (el.classList.contains('drawer') || el.classList.contains('modal') || el.getAttribute('role') === 'dialog') return el; }
    return byName[0] || null;
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) {
      const el = resolveDialog(opener.getAttribute('data-dialog-open'));
      if (el) { e.preventDefault(); Pakhor.dialog.open(el); }
    }
    const closer = e.target.closest('[data-dialog-close]');
    if (closer) {
      const wrap = closer.closest('.drawer[data-open], .modal[data-open], [data-dialog][data-open]');
      if (wrap) { e.preventDefault(); Pakhor.dialog.close(wrap); }
    }
  });

  /* ---------- Intersection observer ---------- */
  Pakhor.observe = function (els, cb, options = { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }) {
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => cb(el, true));
      return { disconnect() {} };
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          cb(entry.target, true);
          io.unobserve(entry.target);
        }
      });
    }, options);
    els.forEach((el) => io.observe(el));
    return io;
  };

  /* ---------- Scroll reveal ---------- */
  if (settings.scroll_reveal_enabled !== false) {
    document.addEventListener('DOMContentLoaded', () => {
      const items = document.querySelectorAll('[data-reveal]');
      Pakhor.observe(Array.from(items), (el) => el.setAttribute('data-revealed', ''));
    });
  }

  /* ---------- Cart count badge updater ---------- */
  Pakhor.on('cart:updated', (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      const count = (cart && cart.item_count) || 0;
      el.textContent = count;
      el.toggleAttribute('data-empty', count === 0);
    });
  });

  /* Initial cart fetch (fire-and-forget) so badges populate after first paint */
  if (settings.routes && settings.routes.cart_url) {
    document.addEventListener('DOMContentLoaded', () => {
      Cart.fetch().catch(() => {});
    });
  }

  /* ---------- Add-to-cart ----------
     Add-to-cart for [data-pakhor-add] forms is owned exclusively by
     pakhor-cart.js (single cart controller). base.js intentionally does
     NOT bind a submit handler here to avoid double add-to-cart. */
})();
